import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import {
  doc,
  getDoc,
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
} from "firebase/firestore";
import { db } from "../../services/firebase/config";
import { getUserProfile } from "../../services/firebase/database";
import {
  Container,
  Typography,
  Grid,
  Avatar,
  Box,
  Card,
  CardContent,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  CircularProgress,
  Button,
  Chip,
} from "@mui/material";
import HomeIcon from "@mui/icons-material/Home";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import PeopleIcon from "@mui/icons-material/People";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import { Link } from "react-router-dom";

function Profile() {
  const user = useSelector((state) => state.auth.user);
  const [profileData, setProfileData] = useState(null);
  const [homeData, setHomeData] = useState(null);
  const [friends, setFriends] = useState([]);
  const [groceryOrders, setGroceryOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProfileData = async () => {
      if (user) {
        try {
          const userDoc = await getUserProfile(user.uid);
          if (userDoc) {
            setProfileData(userDoc);

            // Fetch home data if user has a homeId
            if (userDoc.homeId) {
              const homeDoc = await getDoc(doc(db, "homes", userDoc.homeId));
              if (homeDoc.exists()) {
                setHomeData({ id: homeDoc.id, ...homeDoc.data() });
              }
            }

            if (userDoc.friends && userDoc.friends.length > 0) {
              const friendsData = await Promise.all(
                userDoc.friends.map((friendId) =>
                  getDoc(doc(db, "users", friendId))
                )
              );
              setFriends(
                friendsData
                  .filter((doc) => doc.exists())
                  .map((doc) => ({ id: doc.id, ...doc.data() }))
              );
            }
          }
        } catch (err) {
          setError("Failed to load profile data");
          console.error("Error fetching profile:", err);
        } finally {
          setLoading(false);
        }
      }
    };

    // Set up real-time listener for grocery orders
    let unsubscribeOrders;
    if (user) {
      // Simplified query while indexes are building
      const groceryOrdersQuery = query(
        collection(db, "groceryOrders"),
        where("userId", "==", user.uid)
      );

      unsubscribeOrders = onSnapshot(groceryOrdersQuery, (snapshot) => {
        const orders = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        // Sort client-side until indexes are ready
        const sortedOrders = orders.sort((a, b) => {
          const aTime = a.createdAt?.toDate?.() || new Date(0);
          const bTime = b.createdAt?.toDate?.() || new Date(0);
          return bTime - aTime; // desc order
        });

        setGroceryOrders(sortedOrders);
      });
    }

    fetchProfileData();

    return () => {
      if (unsubscribeOrders) {
        unsubscribeOrders();
      }
    };
  }, [user]);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="80vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="80vh"
      >
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  if (!profileData) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Typography>No profile data available</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 6 }}>
      <Box
        sx={{
          backgroundColor: "#f2f0e4",
          border: "1px solid #000000",
          borderRadius: "16px",
          p: 4,
          position: "relative",
        }}
      >
        <Grid container spacing={4}>
          {/* Left Column - Profile Info */}
          <Grid item xs={12} md={4}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Avatar
                src={profileData.photoURL}
                alt="Profile Photo"
                sx={{
                  width: 120,
                  height: 120,
                  bgcolor: "#bdbdbd",
                  mb: 2,
                }}
              >
                {!profileData.photoURL && "photo"}
              </Avatar>

              <Typography variant="h5" sx={{ fontWeight: "bold", mb: 0.5 }}>
                {profileData.name || "Architect Yis"}
              </Typography>

              <Typography variant="body1" sx={{ mb: 2 }}>
                {profileData.email}
              </Typography>

              <Button
                component={Link}
                to="/edit-profile"
                variant="contained"
                sx={{
                  backgroundColor: "#6495ED",
                  color: "white",
                  borderRadius: "4px",
                  textTransform: "none",
                  px: 3,
                }}
              >
                Edit Profile
              </Button>
            </Box>

            {/* Account Balance Card */}
            <Card
              sx={{
                mt: 4,
                border: "1px solid #000000",
                borderRadius: "8px",
                boxShadow: "none",
                backgroundColor: "transparent",
              }}
            >
              <CardContent>
                <Typography
                  variant="h6"
                  sx={{
                    borderBottom: "1px solid #000000",
                    pb: 1,
                    mb: 2,
                    fontWeight: "bold",
                  }}
                >
                  ACCOUNT BALANCE
                </Typography>
                <Typography variant="h4">
                  {profileData.neighborhoodDollars || 0} ND
                </Typography>
              </CardContent>
            </Card>

            {/* Pool Passes Card */}
            <Card
              sx={{
                mt: 4,
                border: "1px solid #000000",
                borderRadius: "8px",
                boxShadow: "none",
                backgroundColor: "transparent",
              }}
            >
              <CardContent>
                <Typography
                  variant="h6"
                  sx={{
                    borderBottom: "1px solid #000000",
                    pb: 1,
                    mb: 2,
                    fontWeight: "bold",
                  }}
                >
                  POOL PASSES
                </Typography>
                {profileData.poolPasses && profileData.poolPasses.length > 0 ? (
                  profileData.poolPasses.map((pass, idx) => (
                    <Box key={idx} sx={{ mb: 2 }}>
                      <Typography variant="body2">
                        {pass.type === "day" ? "Day Pass" : "Season Pass"} —
                        Purchased:{" "}
                        {new Date(pass.purchasedAt).toLocaleDateString()}
                      </Typography>
                      {pass.filePath && (
                        <Box
                          component="img"
                          src={pass.filePath}
                          alt={pass.type}
                          sx={{
                            width: "100%",
                            height: "auto",
                            borderRadius: 1,
                            mt: 1,
                          }}
                        />
                      )}
                    </Box>
                  ))
                ) : (
                  <Typography variant="body2">No pool passes owned.</Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Right Column - Home and Neighbors */}
          <Grid item xs={12} md={8}>
            {/* Home Card */}
            <Card
              sx={{
                mb: 4,
                border: "1px solid #000000",
                borderRadius: "8px",
                boxShadow: "none",
                backgroundColor: "transparent",
              }}
            >
              <CardContent>
                <Typography
                  variant="h6"
                  sx={{
                    borderBottom: "1px solid #000000",
                    pb: 1,
                    mb: 2,
                    fontWeight: "bold",
                  }}
                >
                  HOME
                </Typography>
                {profileData.homeId && homeData ? (
                  <Box>
                    {/* Display multiple home images */}
                    {homeData.images && homeData.images.length > 0 ? (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          Home Images ({homeData.images.length}):
                        </Typography>
                        <Grid container spacing={2}>
                          {homeData.images.map((imageUrl, index) => (
                            <Grid item xs={12} sm={6} md={4} key={index}>
                              <Box
                                component="img"
                                src={imageUrl}
                                alt={`Home image ${index + 1}`}
                                sx={{
                                  width: "100%",
                                  height: "200px",
                                  objectFit: "cover",
                                  borderRadius: 1,
                                  border: "1px solid #ddd",
                                }}
                              />
                            </Grid>
                          ))}
                        </Grid>
                      </Box>
                    ) : (
                      // Fallback to single image for backward compatibility
                      (profileData.homeImage || homeData.image) && (
                        <Box
                          component="img"
                          src={profileData.homeImage || homeData.image}
                          alt="Home"
                          sx={{
                            width: "100%",
                            height: "auto",
                            objectFit: "contain",
                            borderRadius: 1,
                            mb: 2,
                          }}
                        />
                      )
                    )}

                    <Typography variant="body1">
                      Address: {homeData.address || profileData.address}
                    </Typography>
                    {homeData.price && (
                      <Typography variant="body1">
                        Price: ${homeData.price.toLocaleString()}
                      </Typography>
                    )}
                    {homeData.bedrooms && (
                      <Typography variant="body1">
                        Bedrooms: {homeData.bedrooms}
                      </Typography>
                    )}
                    {homeData.bathrooms && (
                      <Typography variant="body1">
                        Bathrooms: {homeData.bathrooms}
                      </Typography>
                    )}
                    {homeData.neighborhood && (
                      <Typography variant="body1">
                        Neighborhood: {homeData.neighborhood}
                      </Typography>
                    )}
                  </Box>
                ) : (
                  <Typography variant="body1">No home assigned yet</Typography>
                )}
              </CardContent>
            </Card>

            {/* Grocery Orders Card */}
            <Card
              sx={{
                border: "1px solid #000000",
                borderRadius: "8px",
                boxShadow: "none",
                backgroundColor: "transparent",
                mb: 2,
              }}
            >
              <CardContent>
                <Typography
                  variant="h6"
                  sx={{
                    borderBottom: "1px solid #000000",
                    pb: 1,
                    mb: 2,
                    fontWeight: "bold",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <ShoppingCartIcon />
                  GROCERY ORDERS
                </Typography>
                {groceryOrders.length > 0 ? (
                  <Box>
                    {groceryOrders.slice(0, 5).map((order) => (
                      <Box
                        key={order.id}
                        sx={{
                          mb: 2,
                          p: 2,
                          border: "1px solid #e0e0e0",
                          borderRadius: 1,
                        }}
                      >
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="flex-start"
                          mb={1}
                        >
                          <Typography variant="subtitle2" fontWeight="bold">
                            Order #{order.id.slice(-8)}
                          </Typography>
                          <Chip
                            label={order.status}
                            color={
                              order.status === "delivered"
                                ? "success"
                                : order.status === "pending"
                                ? "warning"
                                : "default"
                            }
                            size="small"
                          />
                        </Box>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          mb={1}
                        >
                          {order.createdAt?.toDate().toLocaleDateString()} at{" "}
                          {order.createdAt?.toDate().toLocaleTimeString()}
                        </Typography>
                        <Typography variant="body2" mb={1}>
                          Items:{" "}
                          {order.items
                            ?.map((item) => `${item.name} (${item.quantity})`)
                            .join(", ")}
                        </Typography>
                        <Typography
                          variant="subtitle2"
                          color="primary"
                          fontWeight="bold"
                        >
                          Total: {order.total?.toFixed(2)} ND
                        </Typography>
                      </Box>
                    ))}
                    {groceryOrders.length > 5 && (
                      <Typography variant="caption" color="text.secondary">
                        Showing 5 most recent orders ({groceryOrders.length}{" "}
                        total)
                      </Typography>
                    )}
                  </Box>
                ) : (
                  <Typography variant="body1">
                    No grocery orders yet.{" "}
                    <Button component={Link} to="/grocery-store" size="small">
                      Shop now
                    </Button>
                  </Typography>
                )}
              </CardContent>
            </Card>

            {/* Neighbors Card */}
            <Card
              sx={{
                border: "1px solid #000000",
                borderRadius: "8px",
                boxShadow: "none",
                backgroundColor: "transparent",
              }}
            >
              <CardContent>
                <Typography
                  variant="h6"
                  sx={{
                    borderBottom: "1px solid #000000",
                    pb: 1,
                    mb: 2,
                    fontWeight: "bold",
                  }}
                >
                  NEIGHBORS
                </Typography>
                {friends.length > 0 ? (
                  <List>
                    {friends.map((friend) => (
                      <ListItem key={friend.id}>
                        <ListItemAvatar>
                          <Avatar src={friend.photoURL} />
                        </ListItemAvatar>
                        <ListItemText
                          primary={friend.name}
                          secondary={friend.address}
                        />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body1">
                    No neighbors added yet
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
}

export default Profile;
